import React, { useEffect } from 'react';
import { isAndroid, isIOS } from '@croquiscom/web2app/src/utils/conditions';
import { LogoFastship, LogoStorezigzin, LogoZdelivery } from '@croquiscom-pvt/zds';
import { vars } from '@croquiscom-pvt/zds/tokens';
import { themeTokens } from '@croquiscom-pvt/zds/tokens/zigzag';
import styled from '@emotion/styled';

import { useOnceReveal } from '@common/hooks';
import { Typography } from '@common/styles';
import { BaseText } from '@common/toolbox';
import { displayPrice } from '@common/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { useOptionConfig } from '@domains/pdp-common';

import { PdpDeliveryType } from '../../../graphql/select-option_types.2';
import { SelectBoxItemPreview } from '../select-box-item-preview';

export enum OptionType {
  BASIC = 'BASIC',
  ADDITIONAL = 'ADDITIONAL',
}

interface NotificationInfo {
  product_item_id: string;
  active: boolean;
}

/**
 * @public
 */
export interface SelectBoxItemOptionValue {
  id: string;
  value: string;
  sold_out: boolean;
  disabledPrice?: boolean;
  code?: string | null;
  image_url?: string | null;
  webp_image_url?: string | null;
  price?: number;
  option_type?: OptionType | null;
  notification_info?: NotificationInfo | null;
  delivery_type: PdpDeliveryType | null;
  is_zigzin?: boolean;
  is_last_option?: boolean;
  is_free_shipping?: boolean;
  shipping_fee?: number;
  remain_stock?: number | null;
}

interface Props {
  className?: string;
  disabledPrice?: boolean;
  is_zigzin_product?: boolean;
  isMatchingProduct: boolean;
  isBogo: boolean;
  hasFastShippingOption?: boolean;
  option: SelectBoxItemOptionValue;
  onSelect: (option: SelectBoxItemOptionValue) => void;
  onImpress?: (value: string, is_matching_option: boolean) => void;
}

export const SelectBoxItem = ({
  className,
  is_zigzin_product,
  isMatchingProduct,
  disabledPrice,
  isBogo,
  hasFastShippingOption,
  option,
  onSelect,
  onImpress,
}: Props) => {
  const {
    id,
    value,
    code,
    image_url,
    webp_image_url,
    option_type,
    price,
    shipping_fee,
    remain_stock,
    delivery_type,
    is_zigzin = false,
    is_last_option,
    is_free_shipping,
  } = option;
  const visible_preview = !!(code || image_url || webp_image_url);
  const { targetRef, isRevealed } = useOnceReveal();
  const is_additional_item = option_type === OptionType.ADDITIONAL;
  const is_fast_shipping = delivery_type === PdpDeliveryType.FAST;
  const is_store_zigzin = delivery_type === PdpDeliveryType.VENDOR_FLEX;

  const show_product_price = !disabledPrice && is_last_option && price && !isBogo;
  const show_additional_price = !disabledPrice && is_additional_item && !!price;
  const show_shipping_fee = is_last_option && !!shipping_fee && !is_free_shipping;
  const displayOptionInfo = isMatchingProduct || hasFastShippingOption || is_zigzin_product || is_store_zigzin;
  const { isResize, visibility, isNativePdp } = useOptionConfig();

  useEffect(() => {
    const isAnd = isAndroid();
    const isIos = isIOS();

    // MEMO: AOS 에서 옵션이 미노출된 상태에서도 visibility 값이 visible 이라서 초기 오픈 여부를 isResize 로 판단. (옵션 오픈 전 width 0)
    const isNativeAndroidPdpFirstOpen = isAnd && isNativePdp && isResize;
    const isNativeAndroidPdpReOpen = isNativeAndroidPdpFirstOpen && visibility;
    const isNativeIosPdpOpen = isIos && isNativePdp && visibility;

    if (isRevealed && !isNativePdp) {
      onImpress?.(id, is_zigzin);
      return;
    }

    if (isRevealed && (isNativeAndroidPdpFirstOpen || isNativeAndroidPdpReOpen)) {
      onImpress?.(id, is_zigzin);
      return;
    }

    if (isRevealed && isNativeIosPdpOpen) {
      onImpress?.(id, is_zigzin);
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRevealed, isResize, visibility]);

  return (
    <Container
      className={className}
      ref={targetRef}
      onClick={() => onSelect(option)}
      data-allow-preview
      has_preview={visible_preview}
      displayOptionInfo={displayOptionInfo}
      show_zigzin_style={is_zigzin_product || (is_zigzin && !!image_url)}
      minHeight={displayOptionInfo ? '73px' : undefined}
    >
      {visible_preview && (
        <div className='PreviewContainer'>
          <PreviewContainer>
            <SelectBoxItemPreview code={code} image_url={image_url} webp_image_url={webp_image_url} />
          </PreviewContainer>
        </div>
      )}
      <OptionText variation='BODY_15_MEDIUM' color={themeTokens.v3Color.gray900.value.light}>
        <TextArea>
          <Flex>
            <span>
              <FlexColumn padding={!is_zigzin_product && (code || !!image_url) ? '17px 0' : undefined}>
                <span>{value}</span>
                {displayOptionInfo && (
                  <ShppingTypeTag>
                    {is_store_zigzin && is_zigzin && <LogoStorezigzin width={66} height={11} />}
                    {!is_store_zigzin && is_zigzin && <LogoZdelivery width={57} height={11} />}
                    {!is_zigzin && is_fast_shipping && <LogoFastship width={57} height={11} />}
                    {!is_zigzin && !is_fast_shipping && (
                      <>
                        일반배송
                        {show_shipping_fee && <FeeTag> (+배송 {displayPrice(shipping_fee)})</FeeTag>}
                      </>
                    )}
                  </ShppingTypeTag>
                )}
              </FlexColumn>
            </span>
            <FlexColumn padding={!is_zigzin_product && (code || !!image_url) ? '17px 0' : undefined}>
              {Boolean(show_product_price || show_shipping_fee) && (
                <PriceTag>{Boolean(show_product_price && price) && displayPrice(price ?? 0)}</PriceTag>
              )}
              {Boolean(show_product_price && remain_stock) && (
                <PreSoldOut className={Typography.CAPTION_12_MEDIUM}>{remain_stock}개 남음</PreSoldOut>
              )}
            </FlexColumn>
          </Flex>
        </TextArea>
        {show_additional_price && <PriceText>{displayPrice(price)}</PriceText>}
      </OptionText>
    </Container>
  );
};

const Container = styled.li<{
  has_preview: boolean;
  show_zigzin_style?: boolean;
  displayOptionInfo?: boolean;
  minHeight?: string;
}>`
  border-top: 1px solid ${vars.v3Color.gray100};
  border-radius: 0;
  position: relative;
  display: flex;
  padding: 17px 16px 15px;
  cursor: pointer;
  ${({ minHeight }) => minHeight && `min-height: ${minHeight}`};

  ${({ has_preview }) => has_preview && `padding: 0px 16px;`}

  ${({ show_zigzin_style }) => show_zigzin_style && `padding: 16px; min-height: 74px;`};

  ${({ has_preview, displayOptionInfo }) =>
    has_preview &&
    `
    padding: 0 16px;
    
    .PreviewContainer {
      padding: ${displayOptionInfo ? 17 : 8}px 0;
    }
  `}
  &:first-of-type {
    border-top: none;
  }
`;

const TextArea = styled.span`
  padding: 2px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  word-break: break-all;
`;
const PreviewContainer = styled.div`
  margin-left: -4px;
  margin-right: 8px;
  align-self: center;
`;

const OptionText = styled(BaseText)`
  display: flex;
  flex: 1;
  font-weight: 500;
`;

const PriceText = styled.span`
  margin-left: auto;
  padding-left: 10px;
  font-weight: 600;
  color: ${vars.v3Color.gray500};
  white-space: pre;
`;

const ShppingTypeTag = styled.span`
  margin: 4px 0 0 0;
  width: 100%;
  display: flex;
  font-size: 12px;
  font-weight: 500;
  line-height: 14px;
  color: ${vars.v3Color.gray500};
  white-space: break-spaces;
`;

const Flex = styled.span`
  width: 100%;
  display: flex;
  justify-content: space-between;
`;

const FlexColumn = styled.span<{ padding?: string }>`
  display: flex;
  flex-direction: column;
  ${({ padding }) => padding && `padding: ${padding}`};
`;

const PriceTag = styled.span`
  display: flex;
  flex-direction: column;
  align-items: end;
  min-width: 91px;
  font-size: 15px;
  font-weight: 500;
  color: ${vars.v3Color.gray500};
`;

const FeeTag = styled.span`
  font-size: 12px;
  font-weight: 400;
  color: ${vars.v3Color.gray400};
`;

const PreSoldOut = styled.span`
  margin: 4px 0 0 0;
  display: flex;
  justify-content: end;
  color: ${vars.v3Color.red200};
`;
